import AnkiConnectAPI from '@/app/api/AnkiConnectAPI/AnkiConnectAPI';
import { useDeck } from '@/app/context/DeckContext/useDeck';
import Loader from '@/components/common/loader/Loader';
import AddIcon from '@/icons/AddIcon';
import CheckboxIcon from '@/icons/CheckboxIcon';
import * as React from 'react';
import { buildAnkiNotes } from './utils';
import { useSettings } from '@/app/context/SettingsContext/useSettings';
import { useFeedback } from '@/app/context/FeedbackContext/useFeedback';
import { useModal } from '@/app/context/ModalContext/useModal';
import { useAnalytics } from '@/app/context/AnalyticsContext/useAnalytics';

const AnkiConnectButton = (props: { onClick: () => void }) => {
  const { theme } = useSettings();

  const {
    selectedDeckIndexType,
    selectedDeck,
    media,
    refreshMediaIfExpiring,
    selectedDeckData
  } = useDeck();
  const { openModal } = useModal();
  const [isDisabled, setIsDisabled] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [userMessage, setUserMessage] = React.useState('');

  const { trackEvent, analyticsInstance } = useAnalytics();

  const { submitFeedback } = useFeedback();

  const createAnkiDeck = async (): Promise<boolean> => {
    try {
      const deckName = 'Limbiks::' + selectedDeck.name;

      await refreshMediaIfExpiring();

      await AnkiConnectAPI.createDeck(deckName);

      const notes = await buildAnkiNotes(
        deckName,
        selectedDeckIndexType,
        !!selectedDeck.isImageOcclusion,
        selectedDeckData,
        media
      );

      await AnkiConnectAPI.createNotes(notes);

      return true;
    } catch (e) {
      return false;
    }
  };

  const onClick = async () => {
    try {
      props.onClick();

      setIsDisabled(true);
      setUserMessage('Connecting to AnkiConnect...');

      const alive = await AnkiConnectAPI.ping();

      if (!alive) {
        throw 'Ping failed';
      }

      setUserMessage('Creating Anki Deck...');

      const success = await createAnkiDeck();

      if (!success) {
        throw 'Failed to create anki deck';
      }

      setUserMessage(`Anki deck created successfully`);
      setSuccess(true);

      setTimeout(() => {
        setUserMessage('');
        setIsDisabled(false);
        setSuccess(false);
      }, 3000);

      submitFeedback('Anki Sync Success');

      trackEvent(analyticsInstance, 'anki_connect_success');
    } catch (e) {
      setIsDisabled(false);
      trackEvent(analyticsInstance, 'anki_connect_failed');
      openModal('ankiError');
      submitFeedback('Anki Sync Failed - ' + e.toString());
    }
  };

  return (
    <div
      onClick={isDisabled ? undefined : onClick}
      className={`card min-h-44 max-w-md border-b-4 border-solid border-transparent text-base-content ${
        isDisabled
          ? 'bg-base-300'
          : 'cursor-pointer bg-base-300 hover:border-secondary'
      } text-primary-content shadow-lg lg:card-side `}
    >
      {!isDisabled && (
        <div className="card-body flex flex-row items-center justify-center">
          <div>
            <div className="mr-3 flex flex-row justify-center gap-3">
              <div className="mr-3 flex flex-row justify-center gap-3">
                <AddIcon />
                <p className="text-center text-lg font-bold text-base-content">
                  Create Anki Deck
                </p>
              </div>
            </div>
            <p className="pt-3 text-base-content">
              Sync automatically using AnkiConnect
            </p>
          </div>
        </div>
      )}
      {isDisabled && (
        <div className="card-body flex flex-row items-center justify-center">
          {success ? (
            <CheckboxIcon invert={theme === 'light'} className="pr-1" />
          ) : (
            <Loader />
          )}
          <p className="text-center text-lg font-bold text-base-content">
            {userMessage}
          </p>
        </div>
      )}
    </div>
  );
};

export default AnkiConnectButton;
